// src/components/ServicesSection/ApproveNowModal.jsx
import React, { useRef, useState, useEffect } from "react";
import cancelIcon from "../../../assets/images/cancel.png";
import { useLockBodyScroll } from "../../../hooks/useLockBodyScroll";

const ApproveNowModal = ({
  isOpen,
  onClose,
  logoSrc,
  onApprove,
  onDeny,
  otpRequired = false,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [actionTaken, setActionTaken] = useState(null);
  const inputRefs = useRef([]);
  const timeoutRef = useRef(null);

  // Clear OTP every time modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset OTP to empty
      setOtp(["", "", "", "", "", ""]);
      setOtpError("");
      setFeedback("");
      setActionTaken(null);

      // Auto-focus first OTP input when modal opens (only if OTP is required)
      if (otpRequired && inputRefs.current[0]) {
        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 100);
      }
    }
  }, [isOpen, otpRequired]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (index, value) => {
    // Only allow digits
    if (!/^\d?$/.test(value)) return;

    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setOtpError("");

    // Auto-focus next input if value entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace") {
      e.preventDefault();

      // If current field is empty, move to previous field and clear it
      if (!otp[index] && index > 0) {
        const next = [...otp];
        next[index - 1] = "";
        setOtp(next);
        inputRefs.current[index - 1]?.focus();
      }
      // If current field has value, clear it but stay in same field
      else if (otp[index]) {
        const next = [...otp];
        next[index] = "";
        setOtp(next);
      }
    }

    // Handle left arrow - move to previous input
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle right arrow - move to next input
    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Handle paste event
    if ((e.ctrlKey || e.metaKey) && e.key === "v") {
      e.preventDefault();
      return;
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain");

    // Only allow digits
    const pastedDigits = pastedData.replace(/\D/g, "");

    if (pastedDigits.length === 0) return;

    // Determine if pasted OTP is 4 or 6 digits
    const is4Digit = pastedDigits.length === 4;

    // Clear all inputs first
    const nextOtp = ["", "", "", "", "", ""];

    // Fill OTP inputs with pasted digits
    pastedDigits.split("").forEach((digit, index) => {
      if (index < 6) {
        nextOtp[index] = digit;
      }
    });

    setOtp(nextOtp);
    setOtpError("");

    // Focus on the next empty input or last input
    const nextEmptyIndex = nextOtp.findIndex((val) => val === "");
    const focusIndex =
      nextEmptyIndex !== -1 ? nextEmptyIndex : is4Digit ? 3 : 5;

    if (focusIndex < 6) {
      setTimeout(() => {
        inputRefs.current[focusIndex]?.focus();
      }, 0);
    }
  };

  const validateOTP = () => {
    const otpString = otp.join("").trim();

    // If no OTP entered, that's fine (even if required, user can choose not to enter)
    if (otpString.length === 0) {
      return true;
    }

    // If OTP entered, it must be either 4 or 6 digits
    if (otpString.length !== 4 && otpString.length !== 6) {
      setOtpError("OTP must be 4 or 6 digits");
      return false;
    }

    // Check if all are digits
    if (!/^\d*$/.test(otpString)) {
      setOtpError("Only digits are allowed");
      return false;
    }

    return true;
  };

  const clearOTP = () => {
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    if (otpRequired && inputRefs.current[0]) {
      inputRefs.current[0]?.focus();
    }
  };

  const handleClose = () => {
    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset all state
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setFeedback("");
    setActionTaken(null);

    // Close the modal
    onClose();
  };

  const showFeedbackAndClose = (message) => {
    setFeedback(message);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for closing
    timeoutRef.current = setTimeout(() => {
      handleClose();
    }, 1000); // Keep 1 second delay to show feedback
  };

  const handleApprove = async () => {
    if (actionTaken) return;

    // Validate OTP (if entered)
    if (!validateOTP()) {
      return;
    }

    const filled = otp.join("").trim();

    setOtpError("");
    setActionTaken("approve");

    try {
      // Pass the OTP value to onApprove function (can be empty string)
      const response = await onApprove(filled);

      // Extract message from API response
      let apiMessage = "";

      // Try different possible response structures
      if (response?.data?.message) {
        apiMessage = response.data.message;
      } else if (response?.message) {
        apiMessage = response.message;
      } else if (response?.data) {
        // If data is a string, use it directly
        apiMessage =
          typeof response.data === "string"
            ? response.data
            : JSON.stringify(response.data);
      } else if (response) {
        apiMessage = JSON.stringify(response);
      }

      if (apiMessage) {
        showFeedbackAndClose(apiMessage);
      } else {
        // If no message, close immediately
        handleClose();
      }
    } catch (error) {
      // Use the exact error message
      const errorMessage = error.message || "Approve request failed";
      showFeedbackAndClose(errorMessage);
    }
  };

  const handleDeny = async () => {
    if (actionTaken) return;

    setActionTaken("deny");

    try {
      const response = await onDeny();

      // Extract message from API response
      let apiMessage = "";

      // Try different possible response structures
      if (response?.data?.message) {
        apiMessage = response.data.message;
      } else if (response?.message) {
        apiMessage = response.message;
      } else if (response?.data) {
        // If data is a string, use it directly
        apiMessage =
          typeof response.data === "string"
            ? response.data
            : JSON.stringify(response.data);
      } else if (response) {
        apiMessage = JSON.stringify(response);
      }

      if (apiMessage) {
        showFeedbackAndClose(apiMessage);
      } else {
        // If no message, close immediately
        handleClose();
      }
    } catch (error) {
      // Use the exact error message
      const errorMessage = error.message || "Deny request failed";
      showFeedbackAndClose(errorMessage);
    }
  };

  useLockBodyScroll(isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* modal */}
      <div
        className="relative bg-white rounded-2xl p-6 z-10 w-11/12 max-w-md mx-auto transform -translate-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="relative flex items-center justify-center mb-4">
          <h2 className="text-lg font-semibold">Approve Now!</h2>
          <img
            src={cancelIcon}
            alt="Cancel"
            className="absolute right-3 w-6 h-6 cursor-pointer"
            onClick={handleClose}
          />
        </div>

        {/* logo + (OTP inputs if required) */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-center mb-6 space-y-4 sm:space-y-0 sm:space-x-6">
          {/* Company Logo */}
          <div className="w-20 h-20  rounded-lg flex items-center justify-center mx-auto sm:mx-0">
            <img
              src={logoSrc}
              alt="Company Logo"
              className="max-h-18 object-contain"
            />
          </div>

          {/* OTP Section - Conditionally rendered ONLY when required */}
          {otpRequired && (
            <div className="flex-1 max-w-xs">
              {/* OTP Label and Clear Button in same row */}
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Enter OTP (Optional)
                </label>
                <div className="flex items-center space-x-1">
                  <button
                    type="button"
                    onClick={clearOTP}
                    disabled={actionTaken}
                    className="text-s text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Clear OTP
                  </button>
                </div>
              </div>

              {/* OTP Input Boxes */}
              <div className="flex justify-center sm:justify-start space-x-2 mb-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-10 h-10 text-center text-base font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
                    disabled={actionTaken}
                    aria-label={`OTP digit ${index + 1}`}
                  />
                ))}
              </div>

              {/* Helper text */}
              <p className="text-xs text-gray-500 mt-1 text-center sm:text-left">
                Enter 4 or 6 digit OTP (if you have it)
              </p>

              {/* OTP Error Message */}
              {otpError && (
                <p className="mt-2 text-xs text-red-600 font-medium text-center sm:text-left">
                  {otpError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* buttons */}
        <div className="flex space-x-4">
          <button
            onClick={handleDeny}
            disabled={actionTaken}
            className={`flex-1 text-white py-2.5 rounded-lg font-medium transition ${
              actionTaken
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-red-500 hover:bg-red-600"
            }`}
          >
            Deny
          </button>
          <button
            onClick={handleApprove}
            disabled={actionTaken}
            className={`flex-1 text-white py-2.5 rounded-lg font-medium transition ${
              actionTaken
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            Approve Now
          </button>
        </div>

        {/* feedback message - only show if there's actual content */}
        {feedback && (
          <p
            className={`mt-4 text-center font-medium text-sm ${
              feedback.toLowerCase().includes("failed") ||
              feedback.toLowerCase().includes("error") ||
              feedback.toLowerCase().includes("invalid") ||
              feedback.toLowerCase().includes("expired") ||
              feedback.toLowerCase().includes("false")
                ? "text-red-600"
                : "text-green-800"
            }`}
          >
            {feedback}
          </p>
        )}
      </div>
    </div>
  );
};

export default ApproveNowModal;
